/** Lane T (notifications wave 2): an email click's ?mt= becomes one landed event, then leaves the URL. */
import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";

import api from "@/api/base";
import { ll33Headers } from "./ll33/useLl33";

/** constants/mail_track.py MT_KEY: the click route appends it to the link's target. */
export const MT_KEY = "mt";
/** constants/mail_track.py WD_KEY: navigator.webdriver; true records the landing as headless. */
const WD_KEY = "wd";
/** The trail's query root; rail/mailTrailApi.ts keys under it. */
export const MAIL_TRAIL_KEY = "mail-trail";
const LANDED_URL = "tracking/m/landed/";

const liveMt = () => new URLSearchParams(window.location.search).get(MT_KEY);

const withoutMt = (prev: Record<string, unknown>) => {
  const next = { ...prev };
  delete next[MT_KEY];
  return next;
};

/** PortalLayout, before its early returns. mt is read in the first render: child effects run first and Page's URL
 * writer drops unknown params. Signed out it waits; p2_deep_link carries mt through login. */
export const useMailLanding = (user: unknown) => {
  const [mt] = React.useState(liveMt);
  const sent = React.useRef(false);
  const navigate = useNavigate();
  const client = useQueryClient();
  React.useEffect(() => {
    if (!mt || !user || sent.current) return;
    sent.current = true;
    // Functional on the router's live location, so a replace Page made this tick keeps its ?it= and hash.
    if (liveMt() !== null)
      navigate({
        to: ".",
        search: withoutMt,
        hash: true,
        replace: true,
        resetScroll: false,
        hashScrollIntoView: false,
      });
    api
      .post(
        LANDED_URL,
        { [MT_KEY]: mt, [WD_KEY]: navigator.webdriver === true },
        // Integration's api/base.ts interceptor sends this header (381ce728): drop ll33Headers() when merging there.
        { headers: ll33Headers() },
      )
      .finally(() => client.invalidateQueries({ queryKey: [MAIL_TRAIL_KEY] }))
      .catch(() => undefined);
  }, [mt, user, navigate, client]);
};
